import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    Box,
    Divider,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { ThemeProvider, createTheme, ThemeOptions } from '@mui/material/styles'
import {
    Retrievable,
    Surface,
    SurfaceButtonProps,
    DownloadIcon,
    LinkIcon,
    GraphViewIcon,
    ListViewIcon,
    EMBED_ANALYTICS_COLORS,
} from '../components'
import baseTheme from '../theme.json'
import {
    downloadProjectScansCsv,
    getProjectScans,
    RateLimitedError,
} from '../api'
import { ScansResponse } from '../model'
import { FeedbackContext } from '../feedback'
import { translate } from '../i18n'
import { ProjectAnalyticsProps } from './ProjectAnalytics.types'
import { AnalyticsFilters } from './ProjectAnalyticsFilters.types'
import ProjectAnalyticsFilters, {
    defaultAnalyticsFilters,
} from './ProjectAnalyticsFilters'
import AnalyticsSummary from './AnalyticsSummary'
import ScansChart from './ScansChart'
import ScansList from './ScansList'
import AnalyticsSharePopup from './AnalyticsSharePopup'

type AnalyticsView = 'graph' | 'list'

// Brief cooldown after a successful download; the export endpoint is rate
// limited to 5 requests per minute per IP.
const EXPORT_COOLDOWN_SECONDS = 5

const ANALYTICS_FONT_FAMILY = "'Montserrat', 'Raleway', sans-serif"

export default function ProjectAnalytics({
    projectId,
    embedded = false,
}: ProjectAnalyticsProps) {
    const feedbackContext = useContext(FeedbackContext)
    const [data, setData] = useState<ScansResponse>()
    const [filters, setFilters] = useState<AnalyticsFilters>(
        defaultAnalyticsFilters
    )
    const [view, setView] = useState<AnalyticsView>('graph')
    const [sharing, setSharing] = useState(false)
    const [exportDisabled, setExportDisabled] = useState(false)
    const cooldownTimer = useRef<ReturnType<typeof setTimeout>>()

    // Scope Montserrat (and the embed accent) to the analytics component by
    // rebuilding the theme's typography from the base options so every nested
    // MUI Typography/control inherits the font.
    const analyticsTheme = useMemo(() => {
        const options = baseTheme as ThemeOptions
        return createTheme({
            ...options,
            typography: {
                ...options.typography,
                fontFamily: ANALYTICS_FONT_FAMILY,
            },
            ...(embedded
                ? {
                      palette: {
                          ...options.palette,
                          primary: {
                              main: EMBED_ANALYTICS_COLORS.data,
                              contrastText: EMBED_ANALYTICS_COLORS.onData,
                          },
                      },
                  }
                : {}),
        })
    }, [embedded])

    const encloseIcon = (icon: React.ReactNode) => (
        <Box
            component="span"
            sx={{
                width: 26,
                height: 26,
                mr: 1,
                borderRadius: 2,
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
            }}
        >
            {icon}
        </Box>
    )

    const retrievePromise = useCallback((): Promise<
        ScansResponse | undefined
    > => {
        return getProjectScans(projectId, filters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, filters.from, filters.to, filters.platform])

    const onFiltersChange = useCallback((next: AnalyticsFilters) => {
        setFilters(next)
    }, [])

    const disableExportFor = useCallback((seconds: number) => {
        setExportDisabled(true)
        if (cooldownTimer.current) {
            clearTimeout(cooldownTimer.current)
        }
        cooldownTimer.current = setTimeout(
            () => setExportDisabled(false),
            seconds * 1000
        )
    }, [])

    useEffect(
        () => () => {
            if (cooldownTimer.current) {
                clearTimeout(cooldownTimer.current)
            }
        },
        []
    )

    const onDownloadCsv = useCallback(async () => {
        setExportDisabled(true)
        try {
            await downloadProjectScansCsv(projectId, filters)
            disableExportFor(EXPORT_COOLDOWN_SECONDS)
        } catch (error) {
            if (error instanceof RateLimitedError) {
                feedbackContext.showWarning(error.message)
                disableExportFor(error.retryAfterSeconds)
            } else {
                feedbackContext.showError((error as Error).message)
                setExportDisabled(false)
            }
        }
    }, [projectId, filters, disableExportFor, feedbackContext])

    const buttons = useMemo<SurfaceButtonProps[]>(() => {
        // Secondary buttons render to the left of primary ones in the Surface
        // header, so the CSV export sits left of "Create link" and simply
        // shifts right when the share button is absent (embedded mode).
        const list: SurfaceButtonProps[] = [
            {
                variant: 'primary',
                available: true,
                text: translate('projects.analytics.export.button') as string,
                icon: <DownloadIcon />,
                disabled: exportDisabled,
                onClick: onDownloadCsv,
            },
        ]

        if (!embedded) {
            list.push({
                variant: 'primary',
                available: true,
                text: translate(
                    'projects.analytics.share.create_link'
                ) as string,
                icon: <LinkIcon />,
                onClick: () => setSharing(true),
            })
        }

        return list
    }, [embedded, exportDisabled, onDownloadCsv])

    const viewToggle = (
        <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_event, next: AnalyticsView | null) => {
                if (next) {
                    setView(next)
                }
            }}
        >
            <ToggleButton value="graph">
                {encloseIcon(<GraphViewIcon sx={{ fontSize: 16 }} />)}
                {translate('projects.analytics.views.graph')}
            </ToggleButton>
            <ToggleButton value="list">
                {encloseIcon(<ListViewIcon sx={{ fontSize: 16 }} />)}
                {translate('projects.analytics.views.list')}
            </ToggleButton>
        </ToggleButtonGroup>
    )

    return (
        <ThemeProvider theme={analyticsTheme}>
            <Surface
                title={viewToggle}
                buttons={buttons}
                keepHeaderInline={embedded}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <ProjectAnalyticsFilters
                            onChange={onFiltersChange}
                            embedded={embedded}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Retrievable
                            getPromise={retrievePromise}
                            setData={setData}
                            keepContentOnReload
                            builder={() => {
                                if (!data) {
                                    return (
                                        <Typography
                                            variant="body2"
                                            align="center"
                                            color="text.secondary"
                                        >
                                            {translate(
                                                'projects.analytics.empty'
                                            )}
                                        </Typography>
                                    )
                                }

                                const isEmpty =
                                    data.total_scans === 0 ||
                                    data.scans_over_time.length === 0

                                return (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <AnalyticsSummary
                                                scans={data}
                                                embedded={embedded}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider />
                                        </Grid>
                                        <Grid item xs={12}>
                                            {view === 'list' ? (
                                                <ScansList
                                                    projectId={projectId}
                                                    filters={filters}
                                                    embedded={embedded}
                                                />
                                            ) : isEmpty ? (
                                                <Typography
                                                    variant="body2"
                                                    align="center"
                                                    color="text.secondary"
                                                >
                                                    {translate(
                                                        'projects.analytics.empty'
                                                    )}
                                                </Typography>
                                            ) : (
                                                <ScansChart
                                                    scans={data.scans_over_time}
                                                    embedded={embedded}
                                                />
                                            )}
                                        </Grid>
                                    </Grid>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
                {sharing && (
                    <AnalyticsSharePopup
                        projectId={projectId}
                        onClose={() => setSharing(false)}
                    />
                )}
            </Surface>
        </ThemeProvider>
    )
}
